import React, { useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { Snackbar, Alert, CircularProgress } from '@mui/material';
import { EarthCanvas } from '../canvas';

const SectionBg = styled.div`
  position: relative;
  width: 100%;
  min-height: 480px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
`;

const Container = styled.div`
display: flex;
flex-direction: column;
justify-content: center;
position: relative;
z-index: 1;
align-items: center;
@media (max-width: 900px) {
  padding: 0 0 24px 0;
}
@media (max-width: 600px) {
  padding: 0 0 12px 0;
}
`

const Wrapper = styled.div`
position: relative;
display: flex;
justify-content: space-between;
align-items: center;
flex-direction: column;
width: 100%;
max-width: 1350px;
padding: 0px 0px 80px 0px;
gap: 12px;
@media (max-width: 900px) {
  padding: 0px 0px 40px 0px;
}
@media (max-width: 600px) {
  padding: 0px 0px 16px 0px;
  gap: 6px;
}
`

const Title = styled.div`
font-size: 42px;
text-align: center;
font-weight: 600;
margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
      margin-top: 12px;
      font-size: 32px;
  }
`;

const Desc = styled.div`
    font-size: 18px;
    text-align: center;
    max-width: 600px;
    color: ${({ theme }) => theme.text_secondary};
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 16px;
    }
`;


const ContactForm = styled.form`
  width: 95%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background2}; // Plain color shade for readability
  padding: 32px;
  border-radius: 16px;
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  margin-top: 28px;
  gap: 12px;
`

const ContactTitle = styled.div`
  font-size: 24px;
  margin-bottom: 6px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`

const ContactInput = styled.input`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`

const ContactInputMessage = styled.textarea`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`

const ContactButton = styled.input`
  width: 100%;
  text-decoration: none;
  text-align: center;
  background: hsla(271, 100%, 50%, 1);
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  background: -moz-linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  background: -webkit-linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  padding: 13px 16px;
  margin-top: 2px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
  &:active {
    transform: scale(0.97);
    box-shadow: 0 2px 8px #915EFF44;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`



const Contact = () => {
  //hooks
  const [open, setOpen] = React.useState(false);
  const [alertMsg, setAlertMsg] = React.useState('');
  const [alertType, setAlertType] = React.useState('success');
  const [sending, setSending] = React.useState(false);
  const form = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setAlertMsg('Sending email...');
    setAlertType('info');
    setOpen(true);

    emailjs.sendForm(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      form.current,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    )
      .then((result) => {
        setAlertMsg('Email sent successfully!');
        setAlertType('success');
        setSending(false);
        form.current.reset();
      }, (error) => {
        setAlertMsg('Failed to send email. Please try again.');
        setAlertType('error');
        setSending(false);
      });
  }



  return (
    <SectionBg>
      <Content>
        <Container>
          <Wrapper>
            <Title>Contact</Title>
            <Desc>Feel free to reach out to me for any questions or opportunities!</Desc>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '320px',
                maxWidth: '400px',
                margin: '24px auto'
              }}
            >
              <EarthCanvas />
            </div>
            <ContactForm ref={form} onSubmit={handleSubmit}>
              <ContactTitle>Email Me 🚀</ContactTitle>
              <ContactInput
                placeholder="Your Email"
                name="from_email"
                type="email"
                aria-label="Your Email Address"
                required
                disabled={sending}
              />
              <ContactInput
                placeholder="Your Name"
                name="from_name"
                type="text"
                aria-label="Your Name"
                required
                disabled={sending}
              />
              <ContactInput
                placeholder="Subject"
                name="subject"
                type="text"
                aria-label="Email Subject"
                required
                disabled={sending}
              />
              <ContactInputMessage
                placeholder="Message"
                rows="4"
                name="message"
                aria-label="Your Message"
                required
                disabled={sending}
              />
              <ContactButton
                type="submit"
                value={sending ? "Sending..." : "Send Message"}
                aria-label="Send Email Message"
                disabled={sending}
                style={sending ? { pointerEvents: "none", opacity: 0.7 } : {}}
              />
              {sending && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                  <CircularProgress size={24} color="secondary" />
                </div>
              )}
            </ContactForm>
            <Snackbar
              open={open}
              autoHideDuration={sending ? 2000 : 4000}
              onClose={() => setOpen(false)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setOpen(false)}
                severity={alertType}
                sx={{ width: '100%' }}
              >
                {alertMsg}
              </Alert>
            </Snackbar>
          </Wrapper>
        </Container>
      </Content>
    </SectionBg>
  )
}

export default Contact